import { Fragment, type ComponentType, type ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Cabeçalho de página — componente 06.
 * Faixa branca de 57px com fio inferior: à esquerda a migalha ("Pacientes / Debora Gomes Fernandes"),
 * à direita no máximo três ações, sendo uma primária. O título da tela vive no conteúdo (`PageTitle`),
 * não aqui. Ações que não cabem entram em "mais" — nunca encolhem.
 */

export interface Crumb {
  label: string
  href?: string
}

export interface PageHeaderProps {
  crumbs: Crumb[]
  /** Ações à direita (até três, uma primária). */
  actions?: ReactNode
  /** Conteúdo à esquerda das ações, como o ☰ no celular ou "salvo há 2 minutos". */
  aside?: ReactNode
  /** Componente de link para as migalhas com `href` (ex.: `Link` do React Router com prop `to`). */
  LinkComponent?: ComponentType<{ to: string; className?: string; children: ReactNode }>
  className?: string
}

export function PageHeader({ crumbs, actions, aside, LinkComponent, className }: PageHeaderProps) {
  return (
    <header
      data-slot="page-header"
      className={cn(
        'h-header border-hairline bg-surface max-desk:px-4 sticky top-0 z-10 flex items-center justify-between gap-4 border-b px-7',
        className,
      )}
    >
      <nav
        aria-label="Você está em"
        className="font-body text-secondary flex min-w-0 items-center gap-2 text-[12px] leading-none font-light"
      >
        {crumbs.map((crumb, i) => {
          const last = i === crumbs.length - 1
          const linkClass = 'truncate hover:text-ink'
          return (
            <Fragment key={`${crumb.label}-${i}`}>
              {i > 0 ? <span aria-hidden>/</span> : null}
              {crumb.href && !last ? (
                LinkComponent ? (
                  <LinkComponent to={crumb.href} className={linkClass}>
                    {crumb.label}
                  </LinkComponent>
                ) : (
                  <a href={crumb.href} className={linkClass}>
                    {crumb.label}
                  </a>
                )
              ) : (
                <span
                  className={cn('truncate', last && 'text-ink')}
                  aria-current={last ? 'page' : undefined}
                >
                  {crumb.label}
                </span>
              )}
            </Fragment>
          )
        })}
      </nav>
      <div className="flex shrink-0 items-center gap-[10px]">
        {aside ? (
          <div className="font-body text-eyebrow text-label mr-2 font-light tracking-normal">
            {aside}
          </div>
        ) : null}
        {actions}
      </div>
    </header>
  )
}
